<template>
  <Transition name="fade">
    <div v-if="invitationModal" class="modal-backdrop invitation-backdrop" @click.self="invitationModal = null">
      <section class="invitation-modal" role="dialog" aria-modal="true" aria-labelledby="invitation-modal-title">
        <button class="modal-close" aria-label="关闭" @click="invitationModal = null">×</button>
        <header :class="['invitation-header', { 'is-placeholder': !heroImage || heroImageFailed }]">
          <img v-if="heroImage && !heroImageFailed" :src="heroImage" :alt="activityTitle" @error="heroImageFailed = true" />
          <div class="invitation-header-shade"></div>
          <div class="invitation-header-content">
            <span>{{ activityLabel }}</span>
            <h2 id="invitation-modal-title">{{ activityTitle }}</h2>
            <p>奖励按当前活动规则发放，到账对象见下方说明。</p>
          </div>
        </header>

        <div class="invitation-summary">
          <article><small>邀请注册</small><b>{{ invitationModal.registeredCount ?? 0 }}</b><span>人</span></article>
          <article><small>邀请成功</small><b>{{ invitationModal.qualifiedCount ?? invitationModal.successfulCount ?? 0 }}</b><span>人</span></article>
          <article class="reward"><small>累计奖励</small><b>{{ formatAmount(invitationModal.rewardAmount) }}</b><span>元</span></article>
        </div>

        <div class="invitation-code-row">
          <div><small>我的邀请码</small><b>{{ invitationModal.invitationCode || '--' }}</b></div>
          <button v-if="invitationLink" type="button" @click="copyInvitationLink">复制邀请链接</button>
        </div>

        <section class="invitation-rules">
          <div class="invitation-section-title">
            <div><small>REWARD RULES</small><h3>活动规则</h3></div>
            <span>按当前活动配置参与</span>
          </div>
          <div class="invitation-rule-list">
            <article v-for="(rule, index) in invitationRewardRules" :key="rule.type">
              <i>{{ index + 1 }}</i>
              <div><b>{{ rule.title }}</b><p>{{ rule.condition }}</p><span>{{ rule.result }}</span></div>
            </article>
          </div>
          <p v-if="!invitationRewardRules.length">当前没有开启可参与的奖励。</p>
          <ul>
            <li v-for="note in invitationRewardNotes" :key="note">{{ note }}</li>
          </ul>
        </section>

        <footer><button class="primary" @click="invitationModal = null">我知道了</button></footer>
      </section>
    </div>
  </Transition>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useAppContext } from '../../composables/appContext'
import { INVITATION_REWARD_NOTES, buildInvitationRewardRules } from '../../utils/invitationRewardRules'

const { invitationModal, notify } = useAppContext()
const invitationRewardRules = computed(() => buildInvitationRewardRules(invitationModal.value?.rules))
const invitationRewardNotes = INVITATION_REWARD_NOTES
const invitationLink = computed(() => invitationModal.value?.invitationLink || invitationModal.value?.inviteLink || '')
const heroImageFailed = ref(false)
const activityTitle = computed(() => invitationModal.value?.activity?.title || invitationModal.value?.activityTitle || '邀请好友活动')
const heroImage = computed(() => invitationModal.value?.activity?.coverUrl || '')
const activityLabel = computed(() => {
  const label = String(invitationModal.value?.activity?.activityTag || '').trim()
  return label && !/^\d+$/.test(label) ? label : 'INVITE FRIENDS'
})

watch(heroImage, () => {
  heroImageFailed.value = false
})

function formatAmount(value) {
  const amount = Number(value)
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
}

async function copyInvitationLink() {
  if (!invitationLink.value) return
  try {
    await navigator.clipboard.writeText(invitationLink.value)
    notify('邀请链接已复制')
  } catch {
    notify('复制失败，请手动复制邀请链接')
  }
}
</script>

<style scoped>
.invitation-backdrop { z-index: 108; }
.invitation-modal { position: relative; width: min(760px, 100%); max-height: calc(100vh - 40px); overflow: auto; padding: 0 34px 34px; border-radius: 26px; background: #f7faff; box-shadow: 0 30px 100px rgba(0, 0, 0, .32); }
.invitation-modal .modal-close { z-index: 3; color: #fff; text-shadow: 0 1px 8px rgba(10, 28, 48, .5); }
.invitation-header { position: relative; height: 238px; margin: 0 -34px; overflow: hidden; border-radius: 26px 26px 0 0; background: linear-gradient(135deg, #163b68 0%, #2f7fc4 58%, #78bee9 100%); }
.invitation-header::before, .invitation-header::after { position: absolute; content: ''; border: 1px solid rgba(255, 255, 255, .17); border-radius: 50%; }
.invitation-header::before { width: 330px; height: 330px; right: -100px; top: -210px; }
.invitation-header::after { width: 180px; height: 180px; left: -70px; bottom: -120px; }
.invitation-header > img { width: 100%; height: 100%; object-fit: cover; }
.invitation-header-shade { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(15, 31, 51, .05) 22%, rgba(12, 29, 49, .88) 100%); }
.invitation-header-content { position: absolute; z-index: 1; left: 34px; right: 62px; bottom: 28px; color: #fff; }
.invitation-header-content > span, .invitation-section-title small { color: #8fd0ff; font-size: 10px; font-weight: 700; letter-spacing: 2px; }
.invitation-header-content h2 { margin: 7px 0 6px; color: #fff; font-size: 29px; line-height: 1.2; }
.invitation-header-content p { margin: 0; color: rgba(255, 255, 255, .74); font-size: 13px; }
.invitation-summary { position: relative; z-index: 2; display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: -14px; }
.invitation-summary article { padding: 18px; border: 1px solid #e1eaf4; border-radius: 16px; background: #fff; }
.invitation-summary small { display: block; color: #8998aa; font-size: 11px; }
.invitation-summary b { display: inline-block; margin-top: 8px; color: #34475e; font-size: 25px; font-variant-numeric: tabular-nums; }
.invitation-summary span { margin-left: 5px; color: #8a99aa; font-size: 11px; }
.invitation-summary .reward b { color: #217fd6; }
.invitation-code-row { display: flex; align-items: center; gap: 16px; margin-top: 12px; padding: 15px 18px; border: 1px solid #dce9f8; border-radius: 15px; background: linear-gradient(120deg, #edf6ff, #fff); }
.invitation-code-row > div { flex: 1; min-width: 0; }
.invitation-code-row small, .invitation-code-row b { display: block; }
.invitation-code-row small { color: #8494a8; font-size: 10px; }
.invitation-code-row b { margin-top: 4px; color: #2e425b; font-size: 18px; overflow-wrap: anywhere; }
.invitation-code-row button { flex: none; padding: 10px 14px; border-radius: 10px; color: #207fca; background: #e2f1ff; font-size: 11px; }
.invitation-rules { margin-top: 20px; padding: 22px; border: 1px solid #e1eaf4; border-radius: 20px; background: #fff; }
.invitation-section-title { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; padding-bottom: 15px; border-bottom: 1px solid #edf1f6; }
.invitation-section-title h3 { margin: 4px 0 0; color: #2c3e56; font-size: 19px; }
.invitation-section-title > span { color: #7b91ad; font-size: 11px; }
.invitation-rule-list { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 16px; }
.invitation-rule-list article { display: flex; align-items: flex-start; gap: 10px; min-width: 0; padding: 15px; border-radius: 14px; background: #f5f8fc; }
.invitation-rule-list i { display: grid; place-items: center; flex: 0 0 25px; height: 25px; border-radius: 50%; color: #fff; background: #3289dc; font-size: 11px; font-style: normal; }
.invitation-rule-list article > div { min-width: 0; }
.invitation-rule-list b { color: #34465e; font-size: 13px; }
.invitation-rule-list p { margin: 6px 0; color: #718198; font-size: 11px; line-height: 1.55; }
.invitation-rule-list span { color: #2779c2; font-size: 10px; line-height: 1.5; }
.invitation-rules ul { margin: 16px 0 0; padding: 14px 14px 14px 30px; border-radius: 12px; color: #74859a; background: #f9fbfd; font-size: 10px; line-height: 1.7; }
.invitation-modal footer { display: flex; justify-content: flex-end; margin-top: 18px; }
.invitation-modal footer button { min-width: 120px; padding: 11px 18px; border-radius: 11px; }
@media (max-width: 680px) {
  .invitation-modal { padding: 0 18px 26px; border-radius: 20px; }
  .invitation-header { height: 210px; margin: 0 -18px; border-radius: 20px 20px 0 0; }
  .invitation-header-content { left: 22px; right: 48px; bottom: 24px; }
  .invitation-summary { grid-template-columns: 1fr 1fr; }
  .invitation-summary .reward { grid-column: 1 / -1; }
  .invitation-rule-list { grid-template-columns: 1fr; }
  .invitation-section-title { align-items: flex-start; flex-direction: column; }
  .invitation-code-row { align-items: stretch; flex-direction: column; }
  .invitation-code-row button, .invitation-modal footer button { width: 100%; }
}
</style>
